import { RefObject, useState } from 'react';
import { Button, Divider, Flex, Modal, Stack, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { SearchSelectWithNestedCollapsableChildren } from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/SearchSelectWithNestedCollapsableChildren';
import { convertTreeItemsToNestedOptions } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/RightPanel/utils/convertTreeItemsToNestedOptions';
import { getSelectedItems } from '@dealstack/frontend/shared/components/forms/SearchSelectWithNestedCollapsableChildren/utils';
import { RightPanelAction } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import {
	Header,
	HeaderCloseBtn,
	HeaderTitle,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/styled';
import { RightPanel } from '@dealstack/frontend/shared/layout/DashboardGrid/SidePanels/RightPanel';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import {
	BulkUpdateActionType,
	useBulkUpdateDocumentClassifiers,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useBulkUpdateDocumentClassifiers';
import { BulkUpdateDocumentClassifierAction } from '@dealstack/shared/lib/generated';
import { ClassificationGroupType } from '@dealstack/shared/lib/backendClient';
import { useTranslationDocuments } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { TreeItemsWithMeta } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/utils';

interface Props {
	dealId: string;
	setRightPanelAction: (action: RightPanelAction) => void;
	selectedDocumentIds: string[];
	areAllRowsSelected: boolean;
	selectedTreeItem: TreeItemsWithMeta | undefined;
	treeItems: TreeItemsWithMeta[];
	isTreeItemsMenuLoading: boolean;
	documentsTableDataOperationsRef: RefObject<
		TableOperations<{
			relatedToIds: string[];
			updatedAt: { from: string; to: string };
			mipIds: string[];
		}>
	>;
}

export const BulkClassifier = ({
	dealId,
	setRightPanelAction,
	selectedDocumentIds,
	areAllRowsSelected,
	selectedTreeItem,
	treeItems,
	isTreeItemsMenuLoading,
	documentsTableDataOperationsRef,
}: Props) => {
	const tDocuments = useTranslationDocuments();
	const [selectedDealClassifiers, setSelectedDealClassifiers] = useState<string[]>([]);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [updateAction, setUpdateAction] = useState<BulkUpdateActionType>('none');

	// completed docs are where the classifiers grouped so extract them from the treeItems
	const completedDocs = treeItems.filter(
		(treeItem) => treeItem.groupType === ClassificationGroupType.COMPLETED_DOCUMENTS
	);
	const nestedSelectOptions = convertTreeItemsToNestedOptions(completedDocs[0]?.children);

	const handleApplyClassification = async (action: BulkUpdateActionType) => {
		setUpdateAction(action);
		setShowConfirmModal(true);
	};

	const { runBulkUpdate, isPending } = useBulkUpdateDocumentClassifiers({
		dataOperations: documentsTableDataOperationsRef.current,
		dealClassifierIdsToUpdate: selectedDealClassifiers,
		selectedDocumentIds,
		dealId,
		updateAction,
		bulkUpdateUsingFilters: areAllRowsSelected,
		selectedTreeItem,
		onSuccess: () => {
			showToastV2({
				title: tDocuments('bulk_classify.toast.success.title'),
				message: tDocuments('bulk_classify.toast.success.message'),
				state: 'success',
			});
			setRightPanelAction(RightPanelAction.NONE);
		},
		onError: () => {
			showToastV2({
				title: tDocuments('bulk_classify.toast.error.title'),
				message: tDocuments('bulk_classify.toast.error.message'),
				state: 'error',
			});
		},
	});

	const confirmApplyClassification = async () => {
		setShowConfirmModal(false);
		await runBulkUpdate();
	};

	const handleCancel = () => {
		setRightPanelAction(RightPanelAction.NONE);
	};

	if (isTreeItemsMenuLoading) {
		return (
			<RightPanel isOpened>
				<InlineLoader />
			</RightPanel>
		);
	}

	return (
		<FlexExtended direction="column" h="100%" verticalScroll>
			<Header>
				<HeaderTitle>{tDocuments('bulk_classify.labels.title')}</HeaderTitle>
				<HeaderCloseBtn onClick={handleCancel}>
					<Icon variant="close-v3" />
				</HeaderCloseBtn>
			</Header>
			<Stack gap="xs">
				<Text size="sm" fw={500}>
					{tDocuments('bulk_classify.labels.select_classifiers')}
				</Text>

				<SearchSelectWithNestedCollapsableChildren
					options={nestedSelectOptions}
					value={selectedDealClassifiers}
					onChange={setSelectedDealClassifiers}
					searchPlaceHolderText={tDocuments('bulk_classify.labels.search_classifiers')}
					placeHolderText={tDocuments('bulk_classify.labels.select_classifiers_to_apply')}
					containerWidth="100%"
					allowMultiOptionSelect={true}
				/>
			</Stack>
			{/* Action Buttons */}
			<Stack gap="0.5rem" mt="auto" pt="md">
				<Button
					onClick={() =>
						handleApplyClassification(BulkUpdateDocumentClassifierAction.ADD)
					}
					loading={isPending}
					disabled={selectedDealClassifiers.length === 0}
					style={{ flex: 1 }}
				>
					{tDocuments('bulk_classify.buttons.add_confirmation')}
				</Button>
				<Divider label="OR" labelPosition="center" />
				<Button
					variant="filled"
					loading={isPending}
					onClick={() =>
						handleApplyClassification(BulkUpdateDocumentClassifierAction.REMOVE)
					}
					disabled={selectedDealClassifiers.length === 0}
					style={{ flex: 1 }}
					color="red"
				>
					{tDocuments('bulk_classify.buttons.remove_confirmation')}
				</Button>
			</Stack>
			<Modal
				opened={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				title="Confirm bulk update"
				centered
				size="xl"
			>
				<Stack gap="md">
					<Text>
						{tDocuments('bulk_classify.confirmation.message', {
							action:
								updateAction === BulkUpdateDocumentClassifierAction.ADD
									? 'add'
									: 'remove',
							classifierCount: selectedDealClassifiers.length,
							targetScope: areAllRowsSelected
								? 'all documents'
								: `${selectedDocumentIds.length} selected document(s)`,
						})}
					</Text>

					{selectedDealClassifiers.length > 0 && (
						<Stack gap="xs" pl="md" mah="10rem" style={{ overflow: 'auto' }}>
							{getSelectedItems(
								nestedSelectOptions,
								new Set(selectedDealClassifiers)
							).map((classifier) => (
								<Text key={classifier.id} size="sm">
									• {classifier.name}
								</Text>
							))}
						</Stack>
					)}

					<Flex gap="sm" justify="flex-end">
						<Button variant="outline" onClick={() => setShowConfirmModal(false)}>
							{tDocuments('bulk_classify.buttons.cancel')}
						</Button>
						<Button onClick={confirmApplyClassification} loading={isPending}>
							{tDocuments('bulk_classify.buttons.apply')}
						</Button>
					</Flex>
				</Stack>
			</Modal>
		</FlexExtended>
	);
};
